/**
 * Admin BookingsPage Component
 *
 * Admin page for monitoring and managing platform bookings.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../layout';
import { useAdminBookings, AdminBookingsFilter } from './hooks';

// Format date helper
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const BookingsPage: React.FC = () => {
  const [filter, setFilter] = useState<AdminBookingsFilter>({});
  const [page, setPage] = useState(0);

  const { bookings, stats, totalCount, isLoading, hasMore, totalPages } =
    useAdminBookings(filter, page);

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    active: 'bg-green-100 text-green-800',
    completed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  const paymentColors: Record<string, string> = {
    pending: 'text-yellow-600',
    paid: 'text-green-600',
    refunded: 'text-red-600',
    partial_refund: 'text-orange-600',
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
            <p className="text-sm text-gray-500 mt-1">
              Monitor and manage platform bookings
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div
            onClick={() => setFilter({})}
            className={`bg-white rounded-lg p-4 border cursor-pointer hover:border-primary-300 transition-colors ${
              !filter.status ? 'border-primary-500' : 'border-gray-200'
            }`}
          >
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: 'pending' })}
            className={`bg-white rounded-lg p-4 border cursor-pointer hover:border-yellow-300 transition-colors ${
              filter.status === 'pending' ? 'border-yellow-500' : 'border-gray-200'
            }`}
          >
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: 'confirmed' })}
            className={`bg-white rounded-lg p-4 border cursor-pointer hover:border-blue-300 transition-colors ${
              filter.status === 'confirmed' ? 'border-blue-500' : 'border-gray-200'
            }`}
          >
            <p className="text-sm text-gray-500">Confirmed</p>
            <p className="text-2xl font-bold text-blue-600">{stats.confirmed}</p>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: 'active' })}
            className={`bg-white rounded-lg p-4 border cursor-pointer hover:border-green-300 transition-colors ${
              filter.status === 'active' ? 'border-green-500' : 'border-gray-200'
            }`}
          >
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: 'completed' })}
            className={`bg-white rounded-lg p-4 border cursor-pointer hover:border-gray-400 transition-colors ${
              filter.status === 'completed' ? 'border-gray-500' : 'border-gray-200'
            }`}
          >
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-gray-600">{stats.completed}</p>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: 'cancelled' })}
            className={`bg-white rounded-lg p-4 border cursor-pointer hover:border-red-300 transition-colors ${
              filter.status === 'cancelled' ? 'border-red-500' : 'border-gray-200'
            }`}
          >
            <p className="text-sm text-gray-500">Cancelled</p>
            <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ID, guest, host, or listing..."
                  value={filter.search || ''}
                  onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filter.dateFrom || ''}
                onChange={(e) => setFilter({ ...filter, dateFrom: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={filter.dateTo || ''}
                onChange={(e) => setFilter({ ...filter, dateTo: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Sort */}
            <select
              value={`${filter.sortBy || 'createdAt'}-${filter.sortOrder || 'desc'}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-') as [
                  AdminBookingsFilter['sortBy'],
                  AdminBookingsFilter['sortOrder']
                ];
                setFilter({ ...filter, sortBy, sortOrder });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="checkIn-asc">Check-in (Soonest)</option>
              <option value="checkIn-desc">Check-in (Latest)</option>
              <option value="totalAmount-desc">Highest Amount</option>
              <option value="totalAmount-asc">Lowest Amount</option>
            </select>
          </div>

          <div className="mt-3 text-sm text-gray-500">
            {totalCount} booking{totalCount !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Bookings Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
              <p className="text-gray-500 mt-2">Loading bookings...</p>
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-8 text-center">
              <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No bookings found</p>
              {(filter.search || filter.status || filter.dateFrom || filter.dateTo) && (
                <button
                  onClick={() => setFilter({})}
                  className="mt-2 text-primary-500 hover:text-primary-600"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Booking
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Guest
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Host
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Dates
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Status
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Amount
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr
                        key={booking.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        {/* Booking Info */}
                        <td className="p-4">
                          <Link
                            to={`/admin/bookings/${booking.id}`}
                            className="flex items-center gap-3 hover:text-primary-600"
                          >
                            <div className="w-12 h-12 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
                              <img
                                src={booking.listingPhoto}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate max-w-[180px]">
                                {booking.listingTitle}
                              </p>
                              <p className="text-xs text-gray-500 font-mono">
                                #{booking.id}
                              </p>
                            </div>
                          </Link>
                        </td>

                        {/* Guest */}
                        <td className="p-4">
                          <Link
                            to={`/admin/users/${booking.guestId}`}
                            className="text-sm text-primary-600 hover:underline"
                          >
                            {booking.guestName}
                          </Link>
                        </td>

                        {/* Host */}
                        <td className="p-4">
                          <Link
                            to={`/admin/users/${booking.hostId}`}
                            className="text-sm text-primary-600 hover:underline"
                          >
                            {booking.hostName}
                          </Link>
                        </td>

                        {/* Dates */}
                        <td className="p-4 text-sm">
                          <p className="text-gray-900">
                            {formatDate(booking.checkIn)}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {booking.nights} night{booking.nights !== 1 ? 's' : ''}
                          </p>
                        </td>

                        {/* Status */}
                        <td className="p-4">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              statusColors[booking.status]
                            }`}
                          >
                            {booking.status}
                          </span>
                          {/* Labelled: this is the payment status, while the
                              badge above is the booking status. Unlabelled,
                              the two read as a duplicate whenever they match
                              (e.g. "pending" over "pending"). */}
                          <p
                            className={`text-xs mt-1 ${
                              paymentColors[booking.paymentStatus]
                            }`}
                          >
                            Payment: {booking.paymentStatus.replace('_', ' ')}
                          </p>
                        </td>

                        {/* Amount */}
                        <td className="p-4">
                          <p className="text-sm font-medium text-gray-900">
                            ${booking.pricing.totalAmount}
                          </p>
                          <p className="text-xs text-green-600">
                            Commission: ${booking.pricing.platformCommission}
                          </p>
                        </td>

                        {/* Actions */}
                        <td className="p-4">
                          <Link
                            to={`/admin/bookings/${booking.id}`}
                            className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasMore}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

// Icons
const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

export default BookingsPage;
